import {
  CssBaseline,
  GlobalStyles,
  PaletteMode,
  Snackbar,
  Stack,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";

import Header from "./Header";
import Main from "./Main";
import ProgressDialog from "./ProgressDialog";
import { TransferQueueProvider } from "./app/transferQueue";
import {
  anonymousSession,
  fetchSession,
  login,
  logout,
  Session,
} from "./app/api";

const globalStyles = (
  <GlobalStyles styles={{ "html, body, #root": { height: "100%" } }} />
);

const COLOR_MODE_STORAGE_KEY = "flaredrive-color-mode";

function preferredColorMode(): PaletteMode {
  const storedMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  if (storedMode === "light" || storedMode === "dark") return storedMode;

  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function App() {
  const [search, setSearch] = useState("");
  const [showProgressDialog, setShowProgressDialog] = React.useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [session, setSession] = useState<Session>(anonymousSession);
  const [colorMode, setColorMode] = useState<PaletteMode>(preferredColorMode);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: colorMode,
          primary: { main: "#f38020" },
        },
      }),
    [colorMode]
  );

  useEffect(() => {
    fetchSession().then(setSession).catch(setError);
  }, []);

  useEffect(() => {
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, colorMode);
  }, [colorMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      <TransferQueueProvider>
        <Stack sx={{ height: "100%" }}>
          <Header
            search={search}
            onSearchChange={(newSearch: string) => setSearch(newSearch)}
            setShowProgressDialog={setShowProgressDialog}
            session={session}
            colorMode={colorMode}
            onColorModeToggle={() =>
              setColorMode((mode) => (mode === "dark" ? "light" : "dark"))
            }
            onLogin={async (username, password) => {
              setSession(await login(username, password));
            }}
            onLogout={() => {
              setSession(logout());
            }}
          />
          <Main search={search} onError={setError} session={session} />
        </Stack>
        <Snackbar
          autoHideDuration={5000}
          open={Boolean(error)}
          message={error?.message}
          onClose={() => setError(null)}
        />
        <ProgressDialog
          open={showProgressDialog}
          onClose={() => setShowProgressDialog(false)}
        />
      </TransferQueueProvider>
    </ThemeProvider>
  );
}

export default App;
